import { useRef, useState } from 'react';
import Modalheader from '../../../components/header';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import Button from '../../../../button';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import { Check, CrossIcon, DragIcon, PadLockIcon } from '../../../../../assets/icons';

export const MISSION_NAVBAR_FILTERS_STORAGE_KEY = 'mission_admin_navbar_filters';
export const MISSION_NAVBAR_FILTERS_DEFAULT = ['dateRange'];

export const MISSION_NAVBAR_LOCKED_FILTER = {
  key: 'search',
  label: 'Recherche',
};

export const MISSION_NAVBAR_FILTER_DEFINITIONS = [
  { key: 'dateRange', label: 'Période' },
  { key: 'company', label: 'Entreprise' },
  { key: 'assignedTempWorker', label: 'Intérimaire' },
  { key: 'recruitment', label: 'Recrutement' },
  { key: 'supplier', label: 'Fournisseur' },
  { key: 'contract', label: 'Contrat' },
  { key: 'department', label: 'Département' },
  { key: 'period', label: 'Statut' },
  { key: 'status', label: 'Visibilité' },
  { key: 'employee', label: 'Admin assigné' },
];

const ModalCustomizeMissionNavbarFilters = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();

  const initialFilters = modalData?.visibleNavbarFilters || MISSION_NAVBAR_FILTERS_DEFAULT;
  const [activeFilters, setActiveFilters] = useState([...initialFilters]);
  const [isLoading, setIsLoading] = useState(false);

  const hasChanged =
    JSON.stringify(activeFilters) !== JSON.stringify(initialFilters);

  const dragIndex = useRef(null);

  const availableFilters = MISSION_NAVBAR_FILTER_DEFINITIONS.filter(
    (filter) => !activeFilters.includes(filter.key)
  );

  const addFilter = (key) => {
    setActiveFilters((prev) => [...prev, key]);
  };

  const removeFilter = (key) => {
    setActiveFilters((prev) => prev.filter((filterKey) => filterKey !== key));
  };

  const handleDragStart = (index) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e, overIndex) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === overIndex) return;
    setActiveFilters((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex.current, 1);
      next.splice(overIndex, 0, moved);
      dragIndex.current = overIndex;
      return next;
    });
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  const handleConfirm = () => {
    setIsLoading(true);
    try {
      localStorage.setItem(
        MISSION_NAVBAR_FILTERS_STORAGE_KEY,
        JSON.stringify(activeFilters)
      );
      setModalDataReturn({
        type: 'mission_navbar_filters_updated',
        visibleNavbarFilters: [...activeFilters],
      });
    } finally {
      setIsLoading(false);
      toggle();
    }
  };

  const getFilterLabel = (key) =>
    MISSION_NAVBAR_FILTER_DEFINITIONS.find((filter) => filter.key === key)?.label || key;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Personnaliser les filtres
      </Modalheader>

      <ModalBody>
        <div className="flex gap-4 min-h-[300px] py-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-700 mb-3">Ajouter un filtre</p>
            <div className="flex flex-col gap-1">
              {availableFilters.length === 0 ? (
                <p className="text-sm text-gray-400 italic">
                  Tous les filtres sont affichés dans la navbar
                </p>
              ) : (
                availableFilters.map((filter) => (
                  <div
                    key={filter.key}
                    className="flex items-center justify-between px-3 py-2 rounded hover:bg-gray-50"
                  >
                    <span className="text-sm text-gray-700">{filter.label}</span>
                    <Button size="ICON" color="ICON" onClick={() => addFilter(filter.key)}>
                      <Check wh={20} color="#111827" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="w-px bg-gray-200 flex-shrink-0" />

          <div className="w-[280px] flex-shrink-0">
            <p className="font-medium text-gray-700 mb-3">Filtres affichés</p>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-shrink-0">
                    <DragIcon wh={20} color="#9CA3AF" />
                  </span>
                  <span className="text-sm text-gray-400 truncate">
                    {MISSION_NAVBAR_LOCKED_FILTER.label}
                  </span>
                </div>
                <Button size="ICON" color="ICON">
                  <PadLockIcon wh={20} color="#9CA3AF" />
                </Button>
              </div>

              {activeFilters.length === 0 ? (
                <p className="text-sm text-gray-400 italic px-3 py-2">
                  Aucun autre filtre dans la navbar
                </p>
              ) : (
                activeFilters.map((key, index) => (
                <div
                  key={key}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded cursor-default"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="cursor-grab flex-shrink-0">
                      <DragIcon wh={20} color="#9CA3AF" />
                    </span>
                    <span className="text-sm text-gray-700 truncate">
                      {getFilterLabel(key)}
                    </span>
                  </div>
                  <Button size="ICON" color="ICON" onClick={() => removeFilter(key)}>
                    <CrossIcon wh={20} color="#111827" />
                  </Button>
                </div>
              ))
              )}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>
          Annuler
        </Button>
        {isLoading ? (
          <Button size="LG" color="DISABLED">
            Confirmation...
          </Button>
        ) : (
          <Button
            size="LG"
            color={hasChanged ? 'PRIMARY' : 'DISABLED'}
            onClick={hasChanged ? handleConfirm : undefined}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalCustomizeMissionNavbarFilters;
